import { useEffect, useState, useCallback } from "react";
import client, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Loader2, CalendarOff, Check, X } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  pending: "border-amber-200 text-amber-600",
  approved: "border-emerald-200 text-emerald-600",
  rejected: "border-red-200 text-red-600",
  cancelled: "border-slate-200 text-slate-500",
};

export default function LeaveRequests() {
  const { can, user } = useAuth();
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ workerId: "", startDate: "", endDate: "", type: "holiday", reason: "" });

  const canRequest = can("leave.request");
  const canApprove = can("leave.approve");
  const canPickWorker = can("user.view") && !canRequest ? true : canApprove;

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      client.get("/leave"),
      can("user.view") ? client.get("/users") : Promise.resolve({ data: [] }),
    ]).then(([lv, us]) => {
      setRows(lv.data);
      setWorkers((us.data || []).filter((u) => u.role === "cleaner" || u.role === "supervisor"));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [can]);
  useEffect(load, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      const body = { ...form, workerId: canPickWorker && form.workerId ? form.workerId : user.id };
      await client.post("/leave", body);
      toast.success(t("toast.leaveRequested", "Leave requested"));
      setForm({ workerId: "", startDate: "", endDate: "", type: "holiday", reason: "" });
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const decide = async (id, status) => {
    try { await client.patch(`/leave/${id}`, { status, note: "" }); toast.success(t("toast.leaveDecided", "Leave updated")); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="leave-tab">
      <div>
        <h3 className="font-display text-lg font-bold text-slate-900">{t("leave.title", "Leave requests")}</h3>
        <p className="text-sm text-slate-500">{t("leave.subtitle", "Requests feed scheduling conflict detection.")}</p>
      </div>

      {canRequest && (
        <Card className="border-slate-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            {canPickWorker && (
              <div className="md:col-span-2">
                <Label>{t("leave.worker", "Worker")}</Label>
                <Select value={form.workerId} onValueChange={(v) => setForm({ ...form, workerId: v })}>
                  <SelectTrigger data-testid="leave-worker-select" className="mt-1.5"><SelectValue placeholder={t("cal.worker", "Worker")} /></SelectTrigger>
                  <SelectContent>{workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.displayName}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>{t("leave.type", "Type")}</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="leave-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="holiday">{t("leave.holiday", "Holiday")}</SelectItem>
                  <SelectItem value="sick">{t("leave.sick", "Sick")}</SelectItem>
                  <SelectItem value="other">{t("leave.other", "Other")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t("leave.from", "From")}</Label><Input type="date" data-testid="leave-start-input" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} className="mt-1.5" /></div>
            <div><Label>{t("leave.to", "To")}</Label><Input type="date" data-testid="leave-end-input" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} className="mt-1.5" /></div>
            <Button data-testid="leave-submit-btn" onClick={submit} disabled={busy || !form.startDate || !form.endDate} className="bg-brand hover:bg-brand-dark">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1.5" />{t("leave.request", "Request leave")}</>}
            </Button>
            <div className="md:col-span-6"><Label>{t("leave.reason", "Reason")}</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1.5" /></div>
          </div>
        </Card>
      )}

      <Card className="border-slate-200">
        {loading ? <p className="p-6 text-sm text-slate-500">{t("common.loading", "Loading…")}</p>
          : rows.length === 0 ? (
            <div className="p-10 text-center text-slate-500"><CalendarOff className="h-8 w-8 mx-auto mb-2 text-slate-300" />{t("leave.none", "No leave requests.")}</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {rows.map((r) => (
                <div key={r.id} data-testid={`leave-row-${r.id}`} className="flex items-center justify-between px-4 py-3 gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Badge variant="outline" className={STATUS_STYLE[r.status]}>{t(`leave.${r.status}`, r.status)}</Badge>
                    <span className="font-medium text-slate-800">{r.workerName}</span>
                    <span className="text-sm text-slate-500">{t(`leave.${r.type}`, r.type)} · {r.startDate} → {r.endDate}</span>
                    {r.reason && <span className="text-xs text-slate-400 truncate">{r.reason}</span>}
                  </div>
                  {canApprove && r.status === "pending" && (
                    <div className="flex gap-1.5 shrink-0">
                      <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200" data-testid={`leave-approve-${r.id}`} onClick={() => decide(r.id, "approved")}>
                        <Check className="h-4 w-4 mr-1" />{t("leave.approve", "Approve")}
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200" data-testid={`leave-reject-${r.id}`} onClick={() => decide(r.id, "rejected")}>
                        <X className="h-4 w-4 mr-1" />{t("leave.reject", "Reject")}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
      </Card>
    </div>
  );
}
